import { useState } from 'react'
import type { CheckNote as Note, ReplyCheckAction } from '../../lib/moderation'
import { resolveReplyCheck } from '../../lib/moderation'
import { messageOf } from '../../lib/errors'
import { useHubLang } from '../../lib/i18n'
import { CHECK_NOTE } from '../../lib/hubCopy'

/**
 * What the content check said — 127 (FED-25), moderator mode only.
 *
 * The guidelines' section 9 has said since 29 Aug that moderators "see the
 * check's verdict and reasoning". Until 127 the only place that was true was
 * the queue, and the queue holds flags and blocks — so a moderator looking at
 * a post that went through had no way to see WHY it went through. David, 29
 * Sep, looking at a test post with a swear word in it: "in moderation mode
 * can i see 'mild profanity in a self-referential test post, not directed at
 * anyone'". This is that line.
 *
 * ── THE SAME FOUR RECIPES AS THE QUEUE AND THE BOARD ────────────────────────
 *
 * Passed / flagged / blocked / not checked wear `.vpill--temp-*`, the recipes
 * 098 gave Wiki One's curator mode and QueueItem already uses — one vocabulary
 * across the three moderator surfaces.
 *
 * ── PRESENTATION, NOT A BOUNDARY ────────────────────────────────────────────
 *
 * check_notes() returns nothing to anyone without content.moderate, and
 * resolve_comment_check refuses them. The caller renders this only in the
 * armed mode because a moderator reads the hub like everybody else and the
 * mode is where the instruments are (CLAUDE.md, "Curator mode means off").
 *
 * No author, no history, no count (section 5): a moderator judges the words
 * in front of them.
 */
export function CheckNote({
  note,
  isReply = false,
  onResolved,
}: {
  /** Undefined while loading; null when nothing was ever recorded. */
  note: Note | null | undefined
  isReply?: boolean
  /** Re-read after a resolution. Replies only: a post resolves in the queue. */
  onResolved?: () => void | Promise<void>
}) {
  const w = CHECK_NOTE[useHubLang()]
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (note === undefined) return null
  if (note === null) {
    return (
      <div className="checknote checknote--none">
        <span className="checknote__head">{w.heading}</span>
        <span className="vpill vpill--temp-unchecked">{w.notChecked}</span>
        <p className="checknote__why">{w.noRecord}</p>
      </div>
    )
  }

  const temp = note.failedOpen
    ? 'unchecked'
    : note.verdict === 'pass' ? 'cool' : note.verdict === 'flag' ? 'warm' : 'hot'
  const label = note.failedOpen
    ? w.notChecked
    : note.verdict === 'pass' ? w.pass
    : note.verdict === 'flag' ? w.flag
    : isReply ? w.replyBlock : w.block
  const when = new Date(note.checkedAt)
  const open = isReply && !note.failedOpen && note.verdict !== 'pass' && note.resolution === null

  async function act(action: ReplyCheckAction) {
    setBusy(true)
    setError(null)
    try {
      await resolveReplyCheck(note!.verdictId, action)
      await onResolved?.()
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={`checknote checknote--${temp}`}>
      <p className="checknote__line">
        <span className="checknote__head">{w.heading}</span>
        <span className={`vpill vpill--temp-${temp}`}>{label}</span>
        {note.rulesHit.length > 0 && <span className="checknote__meta">{w.rules(note.rulesHit.join(', '))}</span>}
        <span className="checknote__meta">
          {[
            note.policyVersion !== null ? w.policy(note.policyVersion) : null,
            note.model,
            when.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }),
          ].filter(Boolean).join(' · ')}
        </span>
        {note.resolution && (
          <span className="checknote__meta">
            {w.resolved(w.resolutions[note.resolution] ?? note.resolution)}
          </span>
        )}
      </p>

      {note.reasoning && <p className="checknote__why">{note.reasoning}</p>}

      {note.retractedBody && (
        <blockquote className="checknote__gone">
          <span className="checknote__meta">{w.tookDown}</span> {note.retractedBody}
        </blockquote>
      )}

      {error && <p className="modbar__error" role="alert">{error}</p>}

      {open && (
        <div className="checknote__actions">
          {note.verdict === 'flag' ? (
            <button type="button" className="btn btn--quiet btn--xs" disabled={busy} onClick={() => void act('dismiss')}>
              {w.dismiss}
            </button>
          ) : (
            <>
              <button type="button" className="btn btn--quiet btn--xs" disabled={busy} onClick={() => void act('restore')}>
                {w.restore}
              </button>
              <button type="button" className="btn btn--quiet btn--xs" disabled={busy} onClick={() => void act('uphold')}>
                {w.uphold}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
